import { QueryClient } from '@tanstack/react-query';
import { handleQueryError } from './queryUtils';

/**
 * The single React Query client for the app, in its own module so non-React
 * code — push handlers, provider modules, the multi-tab leader — can read and
 * write the cache (`queryClient.setQueryData`, `invalidateQueries`) without a
 * hook. `Providers.tsx` hands this same instance to `QueryClientProvider`.
 *
 * Default configuration rationale (for future migration slices):
 *   - staleTime 2 min: most portal data changes infrequently; avoids
 *     redundant refetches on component remount while still staying fresh.
 *   - gcTime 5 min (default): unused cache is garbage-collected after 5 min.
 *   - retry 1: one automatic retry before surfacing the error.
 *   - refetchOnWindowFocus off: the portal often sits beside other tools;
 *     aggressive refetching on every Alt-Tab is disruptive.
 *   - throwOnError false (default): errors are returned in the hook result,
 *     not thrown to the nearest error boundary.
 *
 * The global `onError` in `mutations` mirrors the `rtkQueryErrorLogger`
 * middleware: server errors (>= 500) trigger a toast notification. Individual
 * hooks can add local `onError` callbacks for domain-specific behaviour.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 2, // 2 minutes
    },
    mutations: {
      onError: handleQueryError,
    },
  },
});
